/*
================================================================================
FILE HEADER — apps/studio-web/src/lib/assist/resolver.ts
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why                    | What Changed          |
|----|------------|-------------------|------------------------|-----------------------|
| 1  | 2026-07-02 | Claude Sonnet 5   | Phase 6 AI Assist mode | Created — Lane 1      |
|    |            |                   |                        | element resolution    |
|    |            |                   |                        | ladder (exact/contains|
|    |            |                   |                        | /ONNX embedding)       |
| 2  | 2026-07-03 | Claude Sonnet 5   | Bug fix from live      | Added ResolveResult    |
|    |            |                   | Chrome+Google session  | .reason so a failed    |
|    |            |                   |                        | ONNX model load isn't  |
|    |            |                   |                        | indistinguishable from |
|    |            |                   |                        | a genuine no-match     |

FILES THAT IMPORT THIS FILE
-----------------------------
- apps/studio-web/src/lib/assist/buildStep.ts
- apps/studio-web/src/components/AssistBar.tsx
- apps/studio-web/src/lib/assist/__tests__/resolver.test.ts

FUNCTIONS / EXPORTS COVERED
----------------------------
- resolveTarget       — phrase (+ optional role filter) -> ResolveResult
- flattenNodes        — SerializedAXTree -> flat list of { node, path }
- ResolvedMatch / ResolveResult — types

IMPORTANT POINTS
----------------
1. Zero tokens. Ladder: exact accessible-name match -> normalized/contains
   match -> ONNX embedding re-rank (all-MiniLM-L6-v2, via @plex/healing's
   getEmbedder — the SAME model + lazy singleton used by Tier-1 healing).
2. This is a SEPARATE, lighter-weight scorer from packages/healing's
   reRankCandidates(). That function operates on @plex/dal's A11yNode + a
   Selector's Fingerprint (post-hoc healing of an already-recorded step).
   AI Assist has no fingerprint yet at resolution time — it has a phrase and
   a live SerializedAXTree (studio-web's browser-side ARIA type). Reusing
   getEmbedder() avoids loading two copies of the ONNX model.
3. Never auto-picks under ambiguity: `ambiguous:true` means the caller MUST
   show top candidates for a human click, never silently choose one.
4. Accept thresholds (embedding stage): score >= 0.55, margin >= 0.08 between
   top-1 and top-2. Slightly more permissive than Tier-1 healing's 0.60/0.07
   because AI Assist runs at authoring time with a human in the loop to
   reject a bad match — healing runs unattended during CI replay.
================================================================================
*/

import type { SerializedAXTree, SerializedAXNode } from '../../extension/protocol.js';
import { getEmbedder } from '@plex/healing';

export interface ResolvedMatch {
  node: SerializedAXNode;
  path: number[];
  accessibleName: string;
  strategy: 'exact' | 'contains' | 'embedding';
  score: number;
}

export interface ResolveResult {
  matches: ResolvedMatch[];
  ambiguous: boolean;
  /**
   * Set ONLY when matches is empty, to distinguish WHY nothing was found —
   * without this, "the local ONNX model failed to load" and "genuinely no
   * such element on the page" both look like an identical bare "no match"
   * to the user, which makes real failures undebuggable.
   */
  reason?: 'model-unavailable' | 'no-match';
}

interface FlatNode {
  node: SerializedAXNode;
  path: number[];
}

const INTERACTIVE_ROLES = new Set([
  'button', 'link', 'textbox', 'combobox', 'listbox', 'menuitem',
  'checkbox', 'radio', 'switch', 'tab', 'option', 'menuitemcheckbox',
  'menuitemradio', 'searchbox', 'spinbutton', 'slider', 'menu',
]);

const ROLE_EQUIVALENTS: Record<string, string[]> = {
  textbox: ['textbox', 'searchbox'],
  searchbox: ['textbox', 'searchbox'],
};

/** Flattens the ARIA tree into a list of nodes with a stable index path (for re-finding / highlighting). */
export function flattenNodes(
  tree: SerializedAXTree,
  opts: { interactiveOnly?: boolean } = {},
): FlatNode[] {
  const { interactiveOnly = true } = opts;
  const out: FlatNode[] = [];

  function walk(node: SerializedAXNode & { children?: SerializedAXNode[] }, path: number[]): void {
    const isInteractive = INTERACTIVE_ROLES.has(node.role);
    if (!interactiveOnly || isInteractive) {
      if (accessibleName(node)) out.push({ node, path });
    }
    node.children?.forEach((child, i) => walk(child as SerializedAXNode & { children?: SerializedAXNode[] }, [...path, i]));
  }

  walk(tree.root as SerializedAXNode & { children?: SerializedAXNode[] }, []);
  return out;
}

export function accessibleName(node: SerializedAXNode): string {
  return (node.name ?? node.description ?? node.placeholder ?? '').trim();
}

function normalize(s: string): string {
  return s.toLowerCase().trim().replace(/\s+/g, ' ');
}

function roleMatches(nodeRole: string, filter: string): boolean {
  if (nodeRole === filter) return true;
  const equivalents = ROLE_EQUIVALENTS[filter];
  return equivalents ? equivalents.includes(nodeRole) : false;
}

function buildNodeSemanticText(node: SerializedAXNode): string {
  const parts: string[] = [];
  if (node.role) parts.push(node.role);
  if (node.name) parts.push(node.name);
  if (node.description && node.description !== node.name) parts.push(node.description);
  if (node.placeholder) parts.push(node.placeholder);
  const testId = node.attributes?.['data-testid'];
  if (testId) parts.push(testId);
  return parts.join(' ').trim() || 'unknown';
}

function cosineSimilarity(a: Float32Array, b: Float32Array): number {
  let dot = 0, normA = 0, normB = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i]! * b[i]!;
    normA += a[i]! * a[i]!;
    normB += b[i]! * b[i]!;
  }
  const denom = Math.sqrt(normA) * Math.sqrt(normB);
  return denom === 0 ? 0 : Math.max(0, Math.min(1, dot / denom));
}

function toMatch(fn: FlatNode, strategy: ResolvedMatch['strategy'], score: number): ResolvedMatch {
  return { node: fn.node, path: fn.path, accessibleName: accessibleName(fn.node), strategy, score };
}

export interface ResolveOptions {
  /** Search all text-bearing nodes, not just interactive ones (used for "page contains" assertions). */
  includeNonInteractive?: boolean;
  minEmbeddingScore?: number;
  minEmbeddingMargin?: number;
}

/** Stage 1 (exact) + Stage 2 (contains) against a given pool. Null = no match in this pool. */
function matchStringStages(pool: FlatNode[], normPhrase: string): ResolveResult | null {
  const exact = pool.filter(n => normalize(accessibleName(n.node)) === normPhrase);
  if (exact.length === 1) return { matches: [toMatch(exact[0]!, 'exact', 1)], ambiguous: false };
  if (exact.length > 1) return { matches: exact.slice(0, 3).map(n => toMatch(n, 'exact', 1)), ambiguous: true };

  const contains = pool.filter(n => {
    const name = normalize(accessibleName(n.node));
    return name.includes(normPhrase) || normPhrase.includes(name);
  });
  if (contains.length === 1) return { matches: [toMatch(contains[0]!, 'contains', 0.9)], ambiguous: false };
  if (contains.length > 1) {
    // Rank by length-closeness to the phrase — shorter delta = more likely intended match.
    const ranked = contains
      .map(n => ({ n, delta: Math.abs(normalize(accessibleName(n.node)).length - normPhrase.length) }))
      .sort((a, b) => a.delta - b.delta);
    const top = ranked.slice(0, 3).map(r => toMatch(r.n, 'contains', 0.9));
    // Ambiguous unless the closest match is a clear standout (next-closest is much longer delta away)
    const ambiguous = ranked.length > 1 && (ranked[1]!.delta - ranked[0]!.delta) < 3;
    return { matches: top, ambiguous };
  }

  return null; // no match in this pool
}

/**
 * Resolves a natural-language target phrase against the live ARIA tree.
 * Ladder: exact accessible-name match -> contains match -> ONNX embedding re-rank.
 * Returns `ambiguous:true` when multiple candidates tie — the caller must
 * ask the human to pick, never auto-select.
 *
 * Role filter is a HINT, not a hard constraint: if the filtered pool yields no
 * exact/contains match at all, stages 1-2 retry against the full unfiltered
 * pool before falling through to embeddings — real users sometimes get the
 * role noun wrong ("the link 'Sign in'" when it's actually a button), and
 * finding the right element beats rigidly enforcing the stated role.
 */
export async function resolveTarget(
  tree: SerializedAXTree,
  phrase: string,
  roleFilter?: string,
  opts: ResolveOptions = {},
): Promise<ResolveResult> {
  const { includeNonInteractive = false, minEmbeddingScore = 0.55, minEmbeddingMargin = 0.08 } = opts;

  const all = flattenNodes(tree, { interactiveOnly: !includeNonInteractive });
  const filteredPool = roleFilter ? all.filter(n => roleMatches(n.node.role, roleFilter)) : all;

  if (all.length === 0) return { matches: [], ambiguous: false };

  const normPhrase = normalize(phrase);
  if (!normPhrase) return { matches: [], ambiguous: false };

  // Stages 1-2 against the role-filtered pool first, then widen to `all` on a total miss.
  let searchSpace = filteredPool.length > 0 ? filteredPool : all;
  const stringResult = matchStringStages(searchSpace, normPhrase);
  if (stringResult) return stringResult;

  if (searchSpace !== all) {
    const widened = matchStringStages(all, normPhrase);
    if (widened) return widened;
    searchSpace = all; // use the full pool for the embedding stage too
  }

  // ── Stage 3: ONNX embedding re-rank (zero tokens, local, <400ms) ───────────
  let embed: (text: string) => Promise<Float32Array>;
  try {
    embed = await getEmbedder();
  } catch (err) {
    // Model failed to load — @huggingface/transformers fetches the ~23MB
    // all-MiniLM-L6-v2 weights from the HF Hub CDN on first use and caches
    // them in the browser (Cache Storage/IndexedDB) for every call after.
    // If that first download is blocked (offline, restrictive network) or
    // errors for any other reason, this is NOT the same failure as "no such
    // element exists" — surface it distinctly so it's debuggable.
    console.error('[AI Assist] ONNX embedder failed to load — falling back without embedding re-rank:', err);
    return { matches: [], ambiguous: false, reason: 'model-unavailable' };
  }

  const queryVec = await embed(phrase);
  const scored = await Promise.all(
    searchSpace.map(async (n) => {
      const vec = await embed(buildNodeSemanticText(n.node));
      return { fn: n, score: cosineSimilarity(queryVec, vec) };
    }),
  );
  scored.sort((a, b) => b.score - a.score);
  const top = scored.slice(0, 3);

  if (top.length === 0 || top[0]!.score < minEmbeddingScore) return { matches: [], ambiguous: false, reason: 'no-match' };

  const margin = top.length > 1 ? top[0]!.score - top[1]!.score : 1;
  const ambiguous = top.length > 1 && margin < minEmbeddingMargin;

  return {
    matches: top.map(t => toMatch(t.fn, 'embedding', t.score)),
    ambiguous,
  };
}
